// Vandaag als raster (feat/vandaag-raster, oprichter 5 okt 2026: "Vandaag moet net zoals het bord
// aanpasbaar zijn met een raster"). Op een breed scherm staan de kaarten in een raster van 12
// kolommen met rijen van vaste hoogte (de pagina scrolt, dus de rijen lopen door naar beneden);
// elke kaart heeft een plek (x, y) en een maat (w, h) in vakken. Op een tablet worden het 6
// kolommen (omgerekend, zie projectHomeGrid), op de telefoon één kolom in de volgorde van de lijst.
//
// Opslag (geen DB-migratie): dezelfde jsonb `home` in ui_preference.settings als sinds 0042, met
// naast `main` en `side` een lijst `cards`. `main` + `side` blijft de lijst van gekozen kaarten in
// de volgorde van de telefoon (een oudere app leest die gewoon). `cards` zijn de plekken in het
// raster; een indeling zonder `cards` (van vóór het raster) krijgt in de code een raster uit de twee
// kolommen (homeGridFromColumns). Elke wijziging in Aanpassen bewaart daarna `cards` erbij.
//
// De rasterlogica (plaatsen, botsingen, vergroten en verkleinen) is dezelfde motor als op het bord
// (@amumin/domain/grid), met een eigen GridSpec. Puur en zonder Zod.
import { gridClamp, gridCompact, gridMove, gridOverlap, gridPack, gridPlace, gridReadingOrder, gridResize, } from '../grid/index.js';
export const HOME_GRID_COLS = 12;
/** Rijen naar beneden (de pagina scrolt); ruim genoeg om nooit vast te lopen. */
export const HOME_GRID_ROWS = 64;
/** Hoogte van één rij en de ruimte tussen de vakken in px (apps/web, .home-raster). */
export const HOME_GRID_ROW_PX = 56;
export const HOME_GRID_GAP_PX = 16;
/** Kolommen op een tablet (de 12 van de computer, twee aan twee). */
export const HOME_GRID_TABLET_COLS = 6;
/** Maten in drie stappen, zoals de widgets op het bord: klein, middel, groot. */
export const HOME_CARD_SIZES = ['S', 'M', 'L'];
/** Hoe klein en hoe groot een kaart mag (in vakken van 12 kolommen). */
export function homeGridLimits(id) {
    // De agenda van vandaag: minstens vier afspraken onder elkaar.
    if (id === 'agenda')
        return { minW: 4, minH: 3, maxW: 12, maxH: 14 };
    // Ritten zijn één regel: één rij is genoeg.
    if (id === 'rides')
        return { minW: 3, minH: 1, maxW: 12, maxH: 4 };
    // Nu bezig: de focuskaart; hoger dan vier rijen voegt niets toe.
    if (id === 'now')
        return { minW: 3, minH: 2, maxW: 12, maxH: 4 };
    if (id === 'tasks')
        return { minW: 3, minH: 2, maxW: 12, maxH: 12 };
    return { minW: 3, minH: 2, maxW: 12, maxH: 10 };
}
export const HOME_GRID = {
    cols: HOME_GRID_COLS,
    rows: HOME_GRID_ROWS,
    limits: homeGridLimits,
};
/** Hetzelfde raster op een tablet: 6 kolommen, de grenzen gehalveerd (naar boven afgerond). */
export const HOME_GRID_TABLET = {
    cols: HOME_GRID_TABLET_COLS,
    rows: HOME_GRID_ROWS,
    limits: (id) => {
        const l = homeGridLimits(id);
        return {
            minW: Math.ceil(l.minW / 2),
            minH: l.minH,
            maxW: Math.ceil(l.maxW / 2),
            maxH: l.maxH,
        };
    },
};
/** Standaardhoogte (rijen) per kaart: zoveel als de inhoud meestal nodig heeft. */
export function homeCardHeight(id) {
    switch (id) {
        case 'rides':
            return 1;
        case 'now':
        case 'check_in':
        case 'balance':
            return 2;
        case 'agenda':
            return 6;
        case 'tasks':
            return 5;
        default:
            return 3;
    }
}
/**
 * Snelle maten S, M en L (knoppen in Aanpassen): breedte en hoogte in vakken van 12 kolommen.
 * Klein is een kwart van de breedte, middel een derde tot de helft, groot de helft of meer.
 */
export function homeCardPreset(id, size) {
    const l = homeGridLimits(id);
    const base = id === 'agenda'
        ? { S: { w: 4, h: 4 }, M: { w: 6, h: 6 }, L: { w: 8, h: 8 } }
        : id === 'rides'
            ? { S: { w: 3, h: 1 }, M: { w: 6, h: 1 }, L: { w: 8, h: 2 } }
            : id === 'now'
                ? { S: { w: 3, h: 2 }, M: { w: 6, h: 2 }, L: { w: 8, h: 3 } }
                : id === 'tasks'
                    ? { S: { w: 3, h: 3 }, M: { w: 4, h: 4 }, L: { w: 6, h: 6 } }
                    : // Fijnslijpen (7 okt 2026): klein is drie rijen. In twee rijen (128 px) paste na de kop
                        // en de regel "+n meer" geen enkele regel (Morgen toonde alleen "+2 meer").
                        { S: { w: 3, h: 3 }, M: { w: 4, h: 3 }, L: { w: 6, h: 5 } };
    const p = base[size];
    return {
        w: Math.max(l.minW, Math.min(l.maxW, p.w)),
        h: Math.max(l.minH, Math.min(l.maxH, p.h)),
    };
}
/** De maat (S/M/L) die bij een kaart van w x h vakken (12 kolommen) hoort. */
export function homeCardSizeOf(w, h) {
    if (w >= 6 && h >= 4)
        return 'L';
    if (w >= 4 && h >= 2)
        return 'M';
    return 'S';
}
/**
 * De maat van de inhoud uit de echte grootte van de kaart in px (zoals boardTierForPx op het bord):
 * hoe groter, hoe meer detail. Smal of laag = S (compact), breed en hoog = L (alles).
 */
export function homeCardSizeForPx(wPx, hPx) {
    if (wPx >= 520 && hPx >= 260)
        return 'L';
    if (wPx >= 300 && hPx >= 120)
        return 'M';
    return 'S';
}
/** Kaarten die in de standaardindeling in de middelste kolom staan (klein en op zichzelf). */
const MIDDLE = ['weather', 'prayer'];
/**
 * Een raster uit de twee kolommen van vóór het raster (en de standaardindeling): de hoofdkolom
 * links (5 breed), weer en gebedstijden in een smalle middenkolom (3 breed) en de zijkolom rechts
 * (4 breed), elk van boven naar beneden in hun volgorde. Zonder middenkolom: 8 en 4 breed.
 */
export function homeGridFromColumns(main, side) {
    const mid = main.filter((id) => MIDDLE.indexOf(id) >= 0);
    const left = main.filter((id) => MIDDLE.indexOf(id) < 0);
    const columns = mid.length > 0
        ? [
            { ids: left, x: 0, w: 5 },
            { ids: mid, x: 5, w: 3 },
            { ids: side, x: 8, w: 4 },
        ]
        : [
            { ids: left, x: 0, w: 8 },
            { ids: side, x: 8, w: 4 },
        ];
    // Een lege kolom geeft zijn breedte aan de buren (bv. alleen een hoofdkolom: de hele breedte).
    const used = columns.filter((c) => c.ids.length > 0);
    if (used.length === 1) {
        used[0].x = 0;
        used[0].w = 12;
    }
    const desired = [];
    for (const col of used) {
        let y = 0;
        for (const id of col.ids) {
            const h = homeCardHeight(id);
            desired.push({ kind: id, x: col.x, y, w: Math.max(homeGridLimits(id).minW, col.w), h });
            y += h;
        }
    }
    return homeGridPack(desired, main.concat(side));
}
/** Eerst wat een plek heeft, dan de rest op de eerste vrije plek; terug in de volgorde `order`. */
function homeGridPack(desired, order) {
    const placed = desired.filter((d) => typeof d.x === 'number' && typeof d.y === 'number');
    const loose = desired.filter((d) => !(typeof d.x === 'number' && typeof d.y === 'number'));
    const packed = gridPack(HOME_GRID, placed.concat(loose));
    return orderCards(packed, order);
}
function orderCards(cards, order) {
    const out = [];
    for (const id of order) {
        const c = cards.find((it) => it.kind === id);
        if (c)
            out.push(c);
    }
    for (const c of cards)
        if (order.indexOf(c.kind) < 0)
            out.push(c);
    return out;
}
/** Een opgeslagen lijst kaarten opschonen: onbekend, dubbel, buiten de grenzen of overlap valt weg. */
export function cleanHomeCards(raw, isId) {
    if (!Array.isArray(raw))
        return undefined;
    const int = (v) => typeof v === 'number' && Number.isInteger(v);
    const out = [];
    for (const it of raw) {
        if (!it || typeof it !== 'object')
            continue;
        const r = it;
        if (!isId(r.kind) || !int(r.x) || !int(r.y) || !int(r.w) || !int(r.h))
            continue;
        const c = gridClamp(HOME_GRID, { kind: r.kind, x: r.x, y: r.y, w: r.w, h: r.h });
        if (out.some((o) => o.kind === c.kind || gridOverlap(o, c)))
            continue;
        out.push(c);
    }
    return out;
}
/**
 * Het raster voor de kaarten die nu op Vandaag staan (`ids`, in de volgorde van de telefoon). Met
 * opgeslagen plekken: daar (wat er nog geen plek had, zoals een net toegevoegde of een terug-
 * gekomen kaart, op de eerste vrije plek). Zonder: uit de twee kolommen.
 */
export function resolveHomeGrid(main, side, cards) {
    const ids = main.concat(side);
    if (!cards)
        return homeGridFromColumns(main, side);
    const desired = ids.map((id) => {
        const c = cards.find((it) => it.kind === id);
        return c ? { ...c } : { kind: id, w: homeCardPreset(id, 'M').w, h: homeCardHeight(id) };
    });
    return homeGridPack(desired, ids);
}
/** Verplaatsen (in vakken); null = past daar niet. */
export function moveHomeCard(cards, id, x, y) {
    return gridMove(HOME_GRID, cards, id, x, y);
}
/** Nieuwe maat (de beginhoek blijft staan); null = past niet. */
export function resizeHomeCard(cards, id, w, h) {
    return gridResize(HOME_GRID, cards, id, w, h);
}
/** Een kaart op een plek in een maat zetten (slepen); null = past niet. */
export function placeHomeCard(cards, card) {
    return gridPlace(HOME_GRID, cards, card);
}
/** Een kaart erbij op de eerste vrije plek (middelste maat). */
export function addHomeCard(cards, id) {
    if (cards.some((c) => c.kind === id))
        return cards.slice();
    const p = homeCardPreset(id, 'M');
    return gridPack(HOME_GRID, cards.concat([{ kind: id, x: -1, y: -1, w: p.w, h: homeCardHeight(id) }]));
}
/** De volgorde op de telefoon na een wijziging in het raster: de leesvolgorde van het raster. */
export function homeOrderOf(cards) {
    return gridReadingOrder(cards).map((c) => c.kind);
}
/** Zelfde raster (plek en maat van elke kaart, volgorde telt niet)? */
export function sameHomeCards(a, b) {
    if (a.length !== b.length)
        return false;
    return a.every((c) => b.some((d) => d.kind === c.kind && d.x === c.x && d.y === c.y && d.w === c.w && d.h === c.h));
}
// ---- Tablet: 6 kolommen ------------------------------------------------------------------------
/**
 * Het raster van 12 kolommen op een tablet (6 kolommen): twee kolommen van 3 of de hele breedte. Een
 * kaart tot 8 van de 12 breed wordt een halve (3), breder een hele (6); links of rechts naar waar hij
 * op de computer begon. In leesvolgorde neergezet (botst iets, dan de eerste vrije plek) en daarna
 * omhoog geschoven, zodat er geen gaten vallen.
 */
export function projectHomeGrid(cards) {
    const desired = gridReadingOrder(cards).map((c) => {
        const w = c.w > 8 ? 6 : 3;
        const x = w === 6 ? 0 : c.x + c.w / 2 > 6 ? 3 : 0;
        return { kind: c.kind, x, y: c.y, w, h: c.h };
    });
    const packed = gridCompact(gridPack(HOME_GRID_TABLET, desired));
    return orderCards(packed, cards.map((c) => c.kind));
}
/**
 * Een wijziging op de tablet terug naar 12 kolommen: kaarten die op de tablet niet veranderden,
 * houden hun eigen plek en maat; de rest krijgt de dubbele kolom en breedte. Wat daarna botst, gaat
 * naar de eerste vrije plek (nooit iets kwijt).
 */
export function unprojectHomeGrid(full, shownBefore, shownAfter) {
    const desired = shownAfter.map((c) => {
        const was = shownBefore.find((b) => b.kind === c.kind);
        const own = full.find((f) => f.kind === c.kind);
        if (own && was && was.x === c.x && was.y === c.y && was.w === c.w && was.h === c.h)
            return { ...own };
        return { kind: c.kind, x: c.x * 2, y: c.y, w: c.w * 2, h: c.h };
    });
    // Eerst de gewijzigde (die wint), dan de rest op hun eigen plek waar die vrij is.
    const changed = desired.filter((d) => !full.some((f) => sameCard(f, d)));
    const kept = desired.filter((d) => full.some((f) => sameCard(f, d)));
    const packed = gridPack(HOME_GRID, changed.concat(kept));
    return orderCards(packed, shownAfter.map((c) => c.kind));
}
function sameCard(a, b) {
    return a.kind === b.kind && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}
//# sourceMappingURL=homeGrid.js.map